import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { Avatar } from "@/components/Avatar";
import { OpcoesDoPerfil } from "./OpcoesDoPerfil";

type Props = {
  /** O tour existe pra esta organização. */
  tourDisponivel?: boolean;
  /** Ainda não foi visto — mostra o pontinho no avatar. */
  mostrarSeloDoTour?: boolean;
  onIniciarTour?: () => void;
};

/** O tour saiu do Topbar e entrou aqui: numa linha de 64px com menu, título, subtítulo, sino e
 *  avatar, sobrava pouquíssimo pro título em 360px. É ação de descoberta, não de uso diário —
 *  cabe bem no menu do perfil. No celular o mesmo conteúdo abre na gaveta (`GavetaDoPerfil`). */
export function UserMenu({ tourDisponivel, mostrarSeloDoTour, onIniciarTour }: Props = {}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-muted"
        >
          <Avatar src={user?.image} name={user?.name} size="sm" />
          {mostrarSeloDoTour && tourDisponivel && (
            <span className="absolute left-7 top-1 h-2 w-2 rounded-full bg-brand-600 ring-2 ring-white dark:ring-card" />
          )}
          {/* Nome e e-mail só em tela larga: abaixo disso disputam espaço com o switcher no meio da barra. */}
          <div className="hidden max-w-[14rem] text-left xl:block">
            <p className="truncate text-sm font-medium leading-tight text-foreground">{user?.name}</p>
            <p className="truncate text-xs leading-tight text-muted-foreground">{user?.email}</p>
          </div>
          <ChevronDown
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-60 overflow-hidden rounded-xl border border-zinc-200 bg-white p-0 shadow-2xl dark:border-white/10 dark:bg-[#17171c]"
      >
        <div className="border-b border-border px-4 py-3">
          <p className="truncate text-sm font-semibold text-foreground">{user?.name}</p>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
        </div>
        <OpcoesDoPerfil
          aoEscolher={() => setOpen(false)}
          tourDisponivel={tourDisponivel}
          mostrarSeloDoTour={mostrarSeloDoTour}
          onIniciarTour={onIniciarTour}
        />
      </PopoverContent>
    </Popover>
  );
}
